/**
 * V316: Dual-way visibility enforcement utilities
 *
 * discovery_visibility defines visibility boundary:
 * - 1st_only → user exists only in 1st-degree world
 * - 1st_2nd → user exists in 1st + 2nd degree world
 * - 1st_2nd_3rd → user exists in full network (default)
 *
 * A connection is visible ONLY IF both users' visibility settings allow the degree.
 */

export type DiscoveryVisibility = '1st_only' | '1st_2nd' | '1st_2nd_3rd';

/**
 * Check if a given degree is allowed by a visibility setting.
 * Unknown degree fails CLOSED (returns false).
 */
export function isDegreeAllowed(degree: number, visibility: string | null | undefined): boolean {
  const vis = visibility || '1st_2nd_3rd';
  if (degree === 1) return true; // 1st degree always visible
  if (degree === 2) return vis === '1st_2nd' || vis === '1st_2nd_3rd';
  if (degree === 3) return vis === '1st_2nd_3rd';
  return false; // unknown degree = hidden (fail closed)
}

/**
 * Check if a connection at the given degree is visible to both parties.
 * Dual-way: BOTH viewer and target must allow the degree.
 */
export function isConnectionVisible(
  degree: number,
  viewerVisibility: string | null | undefined,
  targetVisibility: string | null | undefined
): boolean {
  return (
    isDegreeAllowed(degree, viewerVisibility) && isDegreeAllowed(degree, targetVisibility)
  );
}

/**
 * Compute trust score for display when viewing another user's profile.
 * Zeroes out degree counts that fall outside their discovery_visibility.
 * Formula: (visible_1st × 10) + (visible_2nd × 1) + verified_bonus
 */
export function computeTrustScoreForOtherUser(opts: {
  friendsCount: number;
  secondDegreeCount: number;
  isVerified: boolean;
  discoveryVisibility: string | null | undefined;
}): number {
  const vis = opts.discoveryVisibility || '1st_2nd_3rd';
  const first = opts.friendsCount ?? 0;
  const second = (vis === '1st_2nd' || vis === '1st_2nd_3rd') ? (opts.secondDegreeCount ?? 0) : 0;
  const verifiedBonus = opts.isVerified ? 100 : 0;
  return first * 10 + second * 1 + verifiedBonus;
}
